import { apiFiles } from "@/api/file.api.ts";
import { EmailFlowTags } from "@/components/email/EmailFlowTags.tsx";
import { FileItem } from "@/components/files/FileItem.tsx";
import { FileUpload } from "@/components/files/FileUpload.tsx";
import { InputWithVariableInsert } from "@/components/input-with-variable-insert/InputWithVariableInsert.tsx";
import {
  ConfirmationModal,
  Modal,
  useModals,
  ModalProps,
  Box,
  Button,
  ButtonLink,
  Input,
  ListItem,
  Select,
  SelectItem,
  Stack,
  Switch,
  Text,
} from "@skailer-com/uikit";
import { getEmailPlaceholderOptions } from "@/components/rich-editor/rich-editor.types.ts";
import { RichEditor } from "@/components/rich-editor/RichEditor.tsx";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import i18n from "@/i18n.ts";
import { ChangeStageModal } from "@/pages/recruit/candidates/widgets/ChangeStageModal.tsx";
import { useEmailIntegrations } from "@/queries/integrations/use-emails.query.ts";
import {
  useCandidateActionCreate,
  useCandidateActionUpdateByFilter,
} from "@/queries/recruit/use-candidate-actions.query.ts";
import { useTemplates } from "@/queries/use-templates.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import {
  ApiActionCreateByFilterPayload,
  ApiActionPayload,
  ApiActionVisibilities,
} from "@/types/action.types.ts";
import { ApiEntityTypes, ApiFile } from "@/types/common.types.ts";
import { ApiEmailBaseSendPayload } from "@/types/integrations/integration.types.ts";
import { ApiCandidateViaApplicantFilter } from "@/types/recruit/candidate.types.ts";
import { ApiTemplate } from "@/types/template.types.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo, useState } from "react";
import { Controller, useFieldArray, useForm, useWatch } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { z } from "zod";

// eslint-disable-next-line react-refresh/only-export-components
export const buildEmailSendSchema = (isSingleEmail: boolean) => {
  const required = i18n.t("errors:field_required");
  const bodySchema = z
    .string({ error: required })
    .refine((value) => value.replace(/<[^>]*>/g, "").trim() !== "", required);

  return z.object({
    from_email: z.string({ error: required }).min(1, required),
    to_email: isSingleEmail
      ? z
          .string({ error: required })
          .min(1, required)
          .pipe(z.email(i18n.t("errors:field_email_required")))
      : z.string().nullable().optional(),
    subject: z.string({ error: required }).min(1, required),
    body: bodySchema,
    cc_email: z.string().nullable().optional(),
    bcc_email: z.string().nullable().optional(),
    thread_id: z.string().nullable().optional(),
    file_uuids: z.array(z.string()).optional(),
    follow_ups: z
      .array(
        z.object({
          delay_days: z
            .number({ error: required })
            .int()
            .min(1, i18n.t("ats:follow_up_delay_error"))
            .max(30, i18n.t("ats:follow_up_delay_error")),
          body: bodySchema,
        })
      )
      .max(5, i18n.t("ats:follow_up_limit_error"))
      .optional(),
  });
};

export type EmailSendSchema = z.infer<ReturnType<typeof buildEmailSendSchema>>;

// eslint-disable-next-line react-refresh/only-export-components
export const buildEmailBasePayload = (
  schema: EmailSendSchema,
  files: ApiFile[]
): ApiEmailBaseSendPayload => ({
  from_email: schema.from_email,
  subject: schema.subject,
  body: schema.body || "",
  cc_email: schema.cc_email || "",
  bcc_email: schema.bcc_email || "",
  file_uuids: files.map((file) => file.uuid),
  thread_id: schema.thread_id || "",
  follow_ups: schema.follow_ups ?? [],
});

// eslint-disable-next-line react-refresh/only-export-components
export const buildSingleEmailActionPayload = (
  baseEmailPayload: ApiEmailBaseSendPayload,
  candidateId: number,
  toEmail: string
): ApiActionPayload => ({
  email: {
    ...baseEmailPayload,
    to_email: toEmail,
    candidate_id: candidateId,
  },
  visibility: ApiActionVisibilities.public,
});

// eslint-disable-next-line react-refresh/only-export-components
export const buildMassEmailActionPayload = (
  baseEmailPayload: ApiEmailBaseSendPayload,
  applicantFilter: ApiCandidateViaApplicantFilter
): ApiActionCreateByFilterPayload => ({
  filter: applicantFilter,
  action: {
    email: baseEmailPayload,
    visibility: ApiActionVisibilities.public,
  },
});

export interface EmailSendModalProps extends ModalProps {
  singleEmail?: {
    candidateId: number;
    applicantEmail: string;
    threadId?: string;
  };
  massEmail?: {
    applicantFilter: ApiCandidateViaApplicantFilter;
    applicantEmails: string[];
    pipelineId: number;
    employeeId: number | undefined;
  };
  subject?: string;
}

export function EmailSendModal({ singleEmail, massEmail, subject, ...props }: EmailSendModalProps) {
  const { close } = useModals();
  const { id } = { ...props };
  const { t } = useTranslation();
  const { handleErrors } = useErrorHandle();
  const {
    data: emailIntegrations,
    isLoading: isEmailIntegrationsLoading,
    refetch: refetchEmailIntegrations,
  } = useEmailIntegrations();
  const { data: emailTemplates } = useTemplates();
  const modalsService = useModals();

  const [files, setFiles] = useState<ApiFile[]>([]);
  const [showCopyFields, setShowCopyFields] = useState(false);
  const [activeFlowIndex, setActiveFlowIndex] = useState(0);

  const emailPlaceholderOptions = useMemo(() => getEmailPlaceholderOptions(t), [t]);

  const inactiveFromEmails =
    emailIntegrations
      ?.filter((integration) => !integration.is_active)
      .map(({ identity }) => identity) ?? [];
  const EmailSendSchemaObject = buildEmailSendSchema(!!singleEmail);

  const {
    setValue,
    getValues,
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<EmailSendSchema>({
    mode: "onSubmit",
    resolver: zodResolver(EmailSendSchemaObject),
    defaultValues: {
      to_email: singleEmail ? singleEmail.applicantEmail : t("ats:multiple_emails"),
      subject: subject || "",
      thread_id: singleEmail && singleEmail.threadId,
      body: "",
      follow_ups: [],
    },
  });

  const { fields: followUpFields, append, remove } = useFieldArray({ control, name: "follow_ups" });
  const followUps = useWatch({ control, name: "follow_ups" }) ?? [];
  const fromEmail = useWatch({ control, name: "from_email" });
  const selectedIntegrationIsInactive = inactiveFromEmails.includes(fromEmail);

  useEffect(() => {
    if (!emailIntegrations) return;

    const selectedEmail = getValues("from_email");
    if (emailIntegrations.length === 0) {
      if (selectedEmail) setValue("from_email", "");
      return;
    }

    if (!emailIntegrations.some(({ identity }) => identity === selectedEmail)) {
      setValue("from_email", emailIntegrations[0].identity);
    }
  }, [emailIntegrations, getValues, setValue]);

  useEffect(() => {
    const handleFocus = () => void refetchEmailIntegrations();
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [refetchEmailIntegrations]);

  const emailIntegrationOptions =
    emailIntegrations?.map((integration) => ({
      id: integration.identity,
      title: integration.identity,
    })) || ([] as SelectItem[]);

  const followUpDelayOptions = Array.from({ length: 30 }, (_, index) => {
    const days = index + 1;

    return {
      id: days,
      title: t("plurals:plural_day", { count: days }),
    };
  });

  const candidateActionCreate = useCandidateActionCreate(control);
  const candidateActionUpdateByFilter = useCandidateActionUpdateByFilter(control);

  const onSubmit = handleSubmit((schema) => {
    if (selectedIntegrationIsInactive) return;

    if (!massEmail && !singleEmail) {
      return;
    }

    const baseEmailPayload = buildEmailBasePayload(schema, files);

    if (massEmail) {
      const emailCount = massEmail.applicantEmails.length;

      modalsService.openModal(ConfirmationModal, {
        question: t("core:send_multiple_emails_text"),
        text: t("plurals:plural_emails_send", { count: emailCount }),
        acceptTitle: t("plurals:plural_emails_send_confirm", { count: emailCount }),
        secondButtonTitle: t("common:back"),
        onAccept: async function () {
          const payload = buildMassEmailActionPayload(baseEmailPayload, massEmail.applicantFilter);

          try {
            await candidateActionUpdateByFilter.mutateAsync(payload);

            analyticsService.trackEvent(analyticEvents.email.sent, {
              [analyticProperties.source]: "Gmail",
              [analyticProperties.actionType]: "Candidate",
              [analyticProperties.count]: emailCount,
              [analyticProperties.title]:
                singleEmail && singleEmail.threadId ? "Reply" : "Initial Email",
            });
            close(id);

            modalsService.openModal(ChangeStageModal, {
              pipelineId: massEmail.pipelineId,
              applicantFilter: massEmail.applicantFilter,
            });
          } catch {
            return;
          }
        },
      });
    } else if (singleEmail) {
      const payload = buildSingleEmailActionPayload(
        baseEmailPayload,
        singleEmail.candidateId,
        schema.to_email || ""
      );

      candidateActionCreate.mutate(
        { id: singleEmail.candidateId, payload },
        {
          onSuccess: () => {
            analyticsService.trackEvent(analyticEvents.email.sent, {
              [analyticProperties.source]: "Gmail",
              [analyticProperties.actionType]: "Candidate",
              [analyticProperties.count]: 1,
              [analyticProperties.title]:
                singleEmail && singleEmail.threadId ? "Reply" : "Initial Email",
            });
            close(id);
          },
        }
      );
    }
  });

  async function removeFile(file: ApiFile) {
    setFiles(files.filter((f) => f.id !== file.id));

    const { error } = await apiFiles.delete(file.uuid);
    if (error) handleErrors(error);
  }

  const handleApplyTemplate = (template: ApiTemplate) => {
    setValue("subject", template.subject ?? "", { shouldValidate: true });
    setValue("body", template.body, { shouldValidate: true });
  };

  const handleApplyFollowUpTemplate = (index: number, template: ApiTemplate) => {
    setValue(`follow_ups.${index}.body`, template.body, { shouldValidate: true });
  };

  const handleAddFlow = () => {
    append({ delay_days: 1, body: "" });
    setActiveFlowIndex(followUpFields.length + 1);
  };

  const handleRemoveFlow = (index: number) => {
    if (activeFlowIndex === index + 1) {
      setActiveFlowIndex(Math.max(0, index));
    } else if (activeFlowIndex > index + 1) {
      setActiveFlowIndex(activeFlowIndex - 1);
    }

    remove(index);
  };

  const initialFlowErrors = [errors.subject?.message, errors.body?.message].filter(
    (message): message is string => Boolean(message)
  );
  const flowTags = followUpFields.map((followUp, index) => ({
    id: followUp.id,
    delayDays: followUps[index]?.delay_days ?? 1,
    errors: [
      errors.follow_ups?.[index]?.delay_days?.message,
      errors.follow_ups?.[index]?.body?.message,
    ].filter((message): message is string => Boolean(message)),
  }));
  const activeFollowUpIndex = activeFlowIndex - 1;
  const hasNoEmailIntegrations = !isEmailIntegrationsLoading && emailIntegrations?.length === 0;
  return (
    <Modal
      {...props}
      layout="stretch"
      size={"xl"}
      title={t("core:send_email")}
      withCloser
      closeByEscEnabled
      actions={
        hasNoEmailIntegrations ? undefined : (
          <Stack gap={"sm"} className={"text-center"}>
            <ServerErrorField errors={errors} />
            <Button
              onClick={onSubmit}
              isLoading={candidateActionCreate.isPending}
              size={"lg"}
              className={"w-full"}
              type={"submit"}
            >
              {t("core:send")}
            </Button>

            {followUpFields.length > 0 && (
              <Text className={"text-secondary text-sm"}>
                {t("ats:follow_ups_immutable_description")}
              </Text>
            )}
          </Stack>
        )
      }
    >
      <>
        <form onSubmit={onSubmit} className={"flex flex-col grow"}>
          <Stack direction={"horizontal"} gap={"sm"} items={"center"} justify={"between"}>
            <Input
              readOnly={!singleEmail}
              placeholder={t("core:email_enter_recipient")}
              label={t("core:email_to")}
              type="email"
              className="w-full"
              {...register("to_email")}
              error={errors.to_email?.message}
            />
            <Stack direction={"horizontal"} gap={"md"} items={"center"}>
              <Switch
                checked={showCopyFields}
                onChange={setShowCopyFields}
                title={t("core:email_cc_email")}
              />
            </Stack>
          </Stack>

          {showCopyFields && (
            <div className={"grid lg:grid-cols-2 gap-md"}>
              <Input
                label={t("core:email_cc_email")}
                type="email"
                {...register("cc_email", {
                  value: undefined,
                })}
                error={errors.cc_email?.message}
              />

              <Input
                label={t("core:email_bcc_email")}
                type="email"
                {...register("bcc_email", {
                  value: undefined,
                })}
                error={errors.bcc_email?.message}
              />
            </div>
          )}

          <Controller
            control={control}
            name={"subject"}
            render={({ field }) => (
              <InputWithVariableInsert
                label={t("core:email_subject")}
                type="text"
                {...field}
                error={errors.subject?.message}
                variableOptions={emailPlaceholderOptions}
              />
            )}
          />

          <EmailFlowTags
            activeFlowIndex={activeFlowIndex}
            initialFlowErrors={initialFlowErrors}
            flows={flowTags}
            onAdd={handleAddFlow}
            onDelete={handleRemoveFlow}
            onSelect={setActiveFlowIndex}
          />

          {activeFlowIndex > 0 && (
            <Controller
              control={control}
              name={`follow_ups.${activeFollowUpIndex}.delay_days`}
              render={({ field: { value, onChange } }) => (
                <Select
                  label={t("ats:follow_up_delay")}
                  options={followUpDelayOptions}
                  emptyTitle={t("common:select")}
                  value={value}
                  onChange={(selectedValue) => onChange(Number(selectedValue))}
                  isClearable={false}
                  error={errors.follow_ups?.[activeFollowUpIndex]?.delay_days?.message}
                />
              )}
            />
          )}

          {activeFlowIndex === 0 &&
            (hasNoEmailIntegrations ? (
              <ListItem
                preTitle={t("core:email_from")}
                titleSlot={
                  <Text className="text-danger font-semibold">
                    {t("ats:email_integration_missing_title")}
                  </Text>
                }
                caption={t("ats:email_integration_missing_description")}
                valueSlot={
                  <ButtonLink href="/account-settings/integrations" blank variant="secondary">
                    {t("integrations:connect")}
                  </ButtonLink>
                }
              />
            ) : (
              <Controller
                render={({ field: { value, onChange } }) => (
                  <Stack gap="xs">
                    <Select
                      label={t("core:email_from")}
                      isLoading={isEmailIntegrationsLoading}
                      options={emailIntegrationOptions}
                      emptyTitle={t("common:select")}
                      value={value}
                      onChange={onChange}
                      required={true}
                      renderSelectedTitle={() => (
                        <Text className={selectedIntegrationIsInactive ? "text-danger" : undefined}>
                          {value}
                        </Text>
                      )}
                      error={selectedIntegrationIsInactive ? undefined : errors.from_email?.message}
                    />
                    {selectedIntegrationIsInactive && (
                      <Text className="text-danger text-sm py-2">
                        <Trans
                          i18nKey="ats:email_integration_expired_error_with_link"
                          components={{
                            reconnect: (
                              <a
                                href="/account-settings/integrations"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="underline hover:no-underline"
                              />
                            ),
                          }}
                        />
                      </Text>
                    )}
                  </Stack>
                )}
                control={control}
                name={"from_email"}
              />
            ))}

          {activeFlowIndex === 0 && (
            <Stack className={"py-md"} gap={"md"}>
              {files.length > 0 && (
                <Stack gap="xs" items={"start"}>
                  {files.map((f) => (
                    <FileItem
                      file={f}
                      onRemove={() => {
                        removeFile(f);
                      }}
                      key={`file${f.id}`}
                    />
                  ))}
                </Stack>
              )}
              <Box className={"flex mt-4"}>
                <Box className={"inline-block"}>
                  <FileUpload
                    entityId={singleEmail ? singleEmail.candidateId : massEmail?.employeeId}
                    entityType={singleEmail ? ApiEntityTypes.candidate : ApiEntityTypes.employee}
                    afterSend={(file) => {
                      if (file) setFiles([...files, file]);
                    }}
                    buttonText={t("common:attach")}
                  />
                </Box>
              </Box>
            </Stack>
          )}
          <Box className={"pt-(--gap-md)"}>
            {activeFlowIndex === 0 ? (
              <Controller
                render={({ field: { value, onChange } }) => (
                  <RichEditor
                    value={value || ""}
                    onChange={onChange}
                    placeholder={t("core:email_enter_body") + "...."}
                    placeholderOptions={emailPlaceholderOptions}
                    emailTemplates={emailTemplates}
                    onApplyTemplate={handleApplyTemplate}
                    error={errors.body?.message}
                  />
                )}
                control={control}
                name={"body"}
              />
            ) : (
              <Controller
                control={control}
                name={`follow_ups.${activeFollowUpIndex}.body`}
                render={({ field: { value, onChange } }) => (
                  <RichEditor
                    value={value || ""}
                    onChange={onChange}
                    placeholder={t("core:email_enter_body") + "...."}
                    placeholderOptions={emailPlaceholderOptions}
                    emailTemplates={emailTemplates}
                    onApplyTemplate={(template) =>
                      handleApplyFollowUpTemplate(activeFollowUpIndex, template)
                    }
                    error={errors.follow_ups?.[activeFollowUpIndex]?.body?.message}
                  />
                )}
              />
            )}
          </Box>
        </form>
      </>
    </Modal>
  );
}
